/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-11 14:21:40
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { FormLayout, TextStyle, Link, Icon } from '@shopify/polaris';
import { ExternalMinor } from '@shopify/polaris-icons';
import { Field } from 'redux-form';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { POSITION_OPTIONS, POSITION_VALUE } from 'src/constants/theme-settings.jsx';
import { COLLECTION_HEADER_VALUE, LAYOUT_OPTIONS, ELEMENTS } from 'src/constants/theme-settings.js';
import { RenderRadioButton2 } from 'src/components/views/common/forms/render-radio-button-2';
import RenderColorPickerItem from 'src/components/views/common/render-color-picker-item';
import { URL } from 'src/constants/links';

type Props = {
  formValues: any,
  updateField: (field: string, value: any) => void
};

type ComposedProps = Props & WithI18nProps;

class CollectionHeaderForm extends Component<ComposedProps> {
  state = {
    isOpenedCheckout: true,
    isOpenedViewCart: true
  };

  allowEnabled = (position: string) => {
    const { formValues, prefix } = this.props;
    const layout = _.get(formValues, `${prefix}.layout`);

    // Show all
    if (
      [
        COLLECTION_HEADER_VALUE.COLLECTION_HEADER_2,
        COLLECTION_HEADER_VALUE.COLLECTION_HEADER_4
      ].includes(layout)
    ) {
      return true;
    }

    // Only show middle-left and middle-right
    if (layout === COLLECTION_HEADER_VALUE.COLLECTION_HEADER_1) {
      return [POSITION_VALUE.MIDDLE_LEFT, POSITION_VALUE.MIDDLE_RIGHT].includes(position);
    }

    // Only show all button in the middle
    if (layout === COLLECTION_HEADER_VALUE.COLLECTION_HEADER_3) {
      return [
        POSITION_VALUE.MIDDLE_LEFT,
        POSITION_VALUE.MIDDLE_RIGHT,
        POSITION_VALUE.MIDDLE_CENTER
      ].includes(position);
    }

    // Show all
    return true;
  };

  /**
   * Build color pickers from form values redux
   * @param {*} formValues
   * @returns
   */
  getColorPickers = prefix => {
    const { i18n, formValues } = this.props;
    const layout = _.get(formValues, `${prefix}.layout`);
    const result = [
      {
        colorValue: _.get(formValues, `${prefix}.backgroundColor`),
        fieldName: `${prefix}.backgroundColor`,
        fieldLabel: i18n.translate('AdminHTML.ThemeSetting.Form.collectionHeader_Color_Item')
      }
    ];

    // Requirement from BA (Angeline)
    if (layout === COLLECTION_HEADER_VALUE.COLLECTION_HEADER_2) {
      result.push({
        colorValue: _.get(
          formValues,
          `${ELEMENTS.COLLECTION_HEADER_TITLE}.boxCollectionTitleColor`
        ),
        fieldName: `${ELEMENTS.COLLECTION_HEADER_TITLE}.boxCollectionTitleColor`,
        fieldLabel: i18n.translate('AdminHTML.ThemeSetting.Form.collectionHeaderTitle_Color_Item2')
      });
    }

    return result;
  };

  rewindPosition = () => {
    const { prefix, updateField } = this.props;
    updateField(`${prefix}.contentPosition`, '');
  };

  /**
   * Get breadcrumb position as a grid radio button
   */
  getPositions = (prefix: string) => {
    const { i18n, formValues } = this.props;
    const layout = _.get(formValues, `${prefix}.layout`);

    // Requirement from BA (Angeline)
    if (layout === COLLECTION_HEADER_VALUE.COLLECTION_HEADER_3) {
      return null;
    }

    return (
      <>
        <p className="form-section__item form-section__item--standard-spacing">
          {i18n.translate('AdminHTML.ThemeSetting.Form.collectionHeader_ContentPosition_Label')}
        </p>
        <div className="grid-radio-button__wrapper" style={{ marginTop: '5px' }}>
          <div className="grid-radio-button">
            {POSITION_OPTIONS.map((option, index) => {
              const { label, value } = option;

              return (
                <Field
                  key={index}
                  invisible={true}
                  name={`${prefix}.contentPosition`}
                  id={value}
                  component={RenderRadioButton2}
                  label={label}
                  disabled={!this.allowEnabled(value)}
                />
              );
            })}
          </div>
        </div>
      </>
    );
  };

  /**
   * Get layout option with translation label
   * @returns
   */
  getLayoutOptions = () => {
    const { i18n } = this.props;
    return LAYOUT_OPTIONS.map(option => {
      const { label, value } = option;

      return { label: i18n.translate(label), value };
    });
  };

  render() {
    const { i18n, prefix, updateField } = this.props;

    return (
      <FormLayout>
        <div>
          <span className="form-link">
            <TextStyle variation="subdued">
              {i18n.translate('AdminHTML.ThemeSetting.Form.collectionHeader_UserGuide')}
            </TextStyle>{' '}
            <Link url={URL['CollectionHeaderThemeSetting']} external>
              {i18n.translate('AdminHTML.ThemeSetting.Common.clickHere')}
              <Icon source={ExternalMinor} color="base" />
            </Link>
            <span style={{ color: '#2C6ECB' }}>.</span>
          </span>
        </div>

        <div className="form-section">
          {this.getPositions(prefix)}
          <div className="form-section" style={{ marginTop: '12px' }}>
            <p className="title title-small">
              {i18n.translate('AdminHTML.ThemeSetting.Form.color_Title')}
            </p>
            {this.getColorPickers(prefix).map((item, index) => (
              <RenderColorPickerItem
                key={index}
                colorValue={item.colorValue}
                fieldName={item.fieldName}
                fieldLabel={item.fieldLabel}
                updateField={updateField}
              />
            ))}
          </div>
        </div>
      </FormLayout>
    );
  }
}

export default withI18n()(CollectionHeaderForm);
